import { useEffect, useState } from "react";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import {
  adminWorkTaskTagsApi,
  type CreateTaskTagAdminDto,
  type TaskTagAdminDto,
  type UpdateTaskTagAdminDto,
} from "@/api/workTasks";
import { TAG_COLORS, DEFAULT_TAG_COLOR_TOKEN, tagColorHex } from "./tagColors";

interface Props {
  open: boolean;
  onClose: () => void;
  /** When supplied, the dialog is in edit mode. */
  tag?: TaskTagAdminDto | null;
  onSaved: () => void;
}

const NAME_MAX = 50;
const DESC_MAX = 200;

/**
 * Tasks v1.1 § 2.3 / § 2.4 — create-or-edit dialog. The two paths share
 * the same form so admins build muscle memory; the only difference is
 * the title and the API method invoked on submit.
 */
export function TagEditorDialog({ open, onClose, tag, onSaved }: Props) {
  const isEdit = Boolean(tag);
  const toast = useToast();

  const [name, setName] = useState("");
  const [color, setColor] = useState<string>(DEFAULT_TAG_COLOR_TOKEN);
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(tag?.name ?? "");
    setColor(tag?.color ?? DEFAULT_TAG_COLOR_TOKEN);
    setDescription(tag?.description ?? "");
    setError(null);
  }, [open, tag]);

  const submit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Name is required.");
      return;
    }
    if (trimmedName.length > NAME_MAX) {
      setError(`Name must be ${NAME_MAX} characters or fewer.`);
      return;
    }
    if (description.length > DESC_MAX) {
      setError(`Description must be ${DESC_MAX} characters or fewer.`);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      if (isEdit && tag) {
        const payload: UpdateTaskTagAdminDto = {
          name: trimmedName,
          color,
          description: description.trim() || null,
        };
        await adminWorkTaskTagsApi.update(tag.id, payload);
        toast.success(`Tag '${trimmedName}' saved.`);
      } else {
        const payload: CreateTaskTagAdminDto = {
          name: trimmedName,
          color,
          description: description.trim() || null,
        };
        await adminWorkTaskTagsApi.create(payload);
        toast.success(`Tag '${trimmedName}' created.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not save tag.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={isEdit ? "Edit tag" : "New tag"}
      description={
        isEdit && tag
          ? `Used by ${tag.totalUsageCount} task${tag.totalUsageCount === 1 ? "" : "s"}.`
          : "Constrained to the Phoenix design palette."
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button onClick={submit} loading={submitting} type="button">
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Name" required htmlFor="tag-name" error={error ?? undefined}>
          <Input
            id="tag-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Follow-up"
            autoFocus
            invalid={Boolean(error)}
            maxLength={NAME_MAX}
          />
        </FormField>

        <FormField label="Color" required>
          <div role="radiogroup" aria-label="Tag color" className="flex flex-wrap gap-space-2">
            {TAG_COLORS.map((c) => {
              const selected = c.token === color;
              return (
                <button
                  key={c.token}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={c.label}
                  onClick={() => setColor(c.token)}
                  className={
                    "h-8 w-8 rounded-full border-2 " +
                    (selected ? "border-onyx" : "border-border-light hover:border-gold")
                  }
                  style={{ backgroundColor: c.hex }}
                />
              );
            })}
          </div>
        </FormField>

        <FormField label="Description" htmlFor="tag-desc">
          <Textarea
            id="tag-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short admin note about when to use this tag."
            maxLength={DESC_MAX}
          />
          <div className="text-label text-gray-mid mt-space-1">
            {description.length}/{DESC_MAX}
          </div>
        </FormField>

        {/* Preview swatch + name so admins see the rendered chip before saving. */}
        <div className="flex items-center gap-space-2 mt-space-2">
          <span
            aria-hidden
            className="inline-block h-3 w-3 rounded-full"
            style={{ backgroundColor: tagColorHex(color) }}
          />
          <span className="text-small text-onyx">
            Preview: <strong>{name || "Tag name"}</strong>
          </span>
        </div>
      </div>
    </Modal>
  );
}
